import {
    Button,
    Col, FormGroup, Label,
} from 'reactstrap';
import React from 'react';
import { Formik, FormikActions } from 'formik';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { NetworkRequestStatus, Roles } from 'constants/literals';

// components
import { BaseFormModalTemplate, FormModalButtons } from './BaseModalTemplate';
import BaseFormModal, { BaseFormModalProps } from './BaseFormModal';
import FormControl from 'components/utils/FormControl';
import RolesChecker from 'components/utils/RolesChecker';
import { CenteredAlert } from 'components/styled';
import { ApiResponse, Exchange, LinkVlanExternalAttribute, ReactApiDataActions } from 'constants/api';

interface InProps {
    linkVlanExternalAttribute: LinkVlanExternalAttribute;
    linkVlanUuid: string;
}
interface EnhanceProps {
    onSubmit: (values: any) => boolean;
    linkVlanExternalAttributeRequest: ApiDataBinding<ApiResponse<Exchange>>;
    apiDataActions: ReactApiDataActions;
}

type Props = InProps & EnhanceProps & WithTranslation & BaseFormModalProps;

const enhance = compose<EnhanceProps & WithTranslation, InProps>(
    withApiData({
        linkVlanExternalAttributeRequest: 'putLinkVlanExternalAttribute',
    }),
    withTranslation()
);

type FormInitialValuesType = {
    attribute_type: string;
    name: string;
    value: string;
    description: string;
};

class LinkVlanExternalAttributesEditModal extends BaseFormModal<Props> {

    componentDidUpdate(prevProps: Readonly<Props>) {
        this.handleFormSubmission(prevProps.linkVlanExternalAttributeRequest.request, this.props.linkVlanExternalAttributeRequest.request, {
            successMessage: this.props.t('linkVlanExternalAttributes.updateSuccessMessage'),
        });
    }

    _handleOnSubmit = (values: any, formikBag: FormikActions<FormInitialValuesType>) => {
        const { linkVlanExternalAttributeRequest, linkVlanExternalAttribute, linkVlanUuid, apiDataActions } = this.props;

        linkVlanExternalAttributeRequest.perform({uuid: linkVlanExternalAttribute.uuid },
            { link_vlan_external_attribute: values }).then((response) => {
                if (response.request.networkStatus === NetworkRequestStatus.Success) {
                    apiDataActions.invalidateCache('getLinkVlanExternalAttributes');
                    apiDataActions.invalidateCache('getLinkVlan', { linkVlanUuid });
                }
            });
    }

    render() {
        const { linkVlanExternalAttribute, t } = this.props;

        return (
            <>
                <Formik
                    ref={(formik) => {
                        this.formik = formik;
                    }}
                    initialValues={
                        {
                            attribute_type: linkVlanExternalAttribute.attribute_type,
                            name: linkVlanExternalAttribute.name,
                            value: linkVlanExternalAttribute.value,
                            description: linkVlanExternalAttribute.description,
                        } as FormInitialValuesType
                    }
                    onSubmit={this._handleOnSubmit}
                >
                    {({ values, errors, isSubmitting, touched, handleSubmit, dirty }) => (
                        <BaseFormModalTemplate
                            isOpen={this.state.isOpen}
                            toggle={isSubmitting ? undefined : this.toggle}
                            onClosed={this.handleModalClose}
                            handleSubmit={handleSubmit}
                            header={t('linkVlanExternalAttributes.updateModalTitle')}
                            body={
                                <>
                                    <div>
                                        <FormGroup row>
                                            <Col md={3}>
                                                <Label htmlFor="attribute_type">
                                                    {t('linkVlanExternalAttributes.attributeType')}
                                                </Label>
                                            </Col>
                                            <Col md={9}>
                                                <FormControl
                                                    name="attribute_type"
                                                    errors={errors}
                                                    touched={touched}
                                                    required
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Col md={3}>
                                                <Label htmlFor="name">
                                                    {t('linkVlanExternalAttributes.attributeName')}
                                                </Label>
                                            </Col>
                                            <Col md={9}>
                                                <FormControl name="name" errors={errors} touched={touched} required />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Col md={3}>
                                                <Label htmlFor="value">
                                                    {t('linkVlanExternalAttributes.attributeValue')}
                                                </Label>
                                            </Col>
                                            <Col md={9}>
                                                <FormControl name="value" errors={errors} touched={touched} required />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Col md={3}>
                                                <Label htmlFor="description">
                                                    {t('linkVlanExternalAttributes.attributeDescription')}
                                                </Label>
                                            </Col>
                                            <Col md={9}>
                                                <FormControl name="description" errors={errors} touched={touched} />
                                            </Col>
                                        </FormGroup>
                                        <CenteredAlert color="warning">
                                            {t('linkVlanExternalAttributes.noteMandatoryFields')}
                                        </CenteredAlert>
                                    </div>
                                </>
                            }
                            footer={<FormModalButtons loading={isSubmitting} toggle={this.toggle} disabled={!dirty} />}
                        />
                    )}
                </Formik>
                <RolesChecker data-test="user-roles-checker" roles={[Roles.admin, Roles.noc]}>
                    <span data-test="linkVlan-attribute-edit-btn" className="fas fa-edit fa-pull-left mt-1 text-primary cursor-pointer" 
                          onClick={this.toggle} title={'archiveMemberLink.modalTitle'}>
                    </span>
                </RolesChecker>
            </>
        );
    }
}

export default enhance(LinkVlanExternalAttributesEditModal);
